import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import React, { Fragment, useMemo } from "react";
import { type ICharacter } from "~/data/useCharacterData";
import { env } from "~/env.mjs";
import useUser from "~/lib/useUser";
import { classNames } from "~/utils";
import { InventoryItem, InventoryItemSkeleton } from "./InventoryItem";

type PublicNpcsResponse = {
  assistants: ICharacter[];
  page: number;
  totalPages: number;
  totalRecords: number;
};

const NPCInventory: React.FC<{
  mockAddress?: string;
  itemProps?: {
    actionsComponent: (props: Partial<ICharacter>) => React.ReactNode;
  };
}> = ({ ...props }) => {
  const { itemProps } = props;
  const { user } = useUser();

  const [selectedCategory, setSelectedCategory] = React.useState<string>("All");

  const fetchPublicNpcs = async ({
    pageParam = "",
  }: {
    pageParam?: string;
  }) => {
    const url = `${env.NEXT_PUBLIC_PORTAL_API_URL}/api/v2/assistant/showcase`;
    const { data } = await axios.get<PublicNpcsResponse>(url, {
      params: {
        page: pageParam ? pageParam : "1",
        limit: "50",
      },
      headers: {
        Authorization: `Bearer ${user?.accessToken || ""}`,
      },
    });
    return data;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["characters", "showcase"],
    queryFn: fetchPublicNpcs,
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.page || 1;
      return currentPage === lastPage?.totalPages ? undefined : currentPage + 1;
    },
  });

  const categories = useMemo(() => {
    const items = new Set<string>(["All"]);
    data?.pages?.forEach((response) => {
      response?.assistants.forEach((item) => {
        if (item.category) {
          items.add(item.category);
        }
      });
    });

    return Array.from(items);
  }, [data?.pages]);

  const handleFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!(event.target instanceof HTMLButtonElement)) return;
    setSelectedCategory(event.target.dataset.value as string);
  };

  return (
    <div className="space-y-6">
      <span className="relative z-0 rounded-lg bg-dark-400 shadow-sm">
        {categories?.map((item) => {
          return (
            <button
              data-value={item}
              onClick={handleFilter}
              key={item}
              className={classNames(
                selectedCategory === item
                  ? "z-10 bg-primary-100  ring-primary-100 "
                  : "bg-dark-400 ",
                "relative inline-flex items-center rounded-lg px-1 py-2 text-[0.7rem] font-semibold text-light-100 hover:bg-dark-50 md:px-4 md:text-sm md:font-bold"
              )}
            >
              {item}
            </button>
          );
        })}
      </span>
      <div className="grid min-h-[208px] grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
        {(isLoading || isFetching) && !isFetchingNextPage ? (
          <>
            {Array.from(Array(10).keys()).map((i) => (
              <InventoryItemSkeleton key={i} />
            ))}
          </>
        ) : (
          <>
            {data?.pages?.map((response, i) => (
              <Fragment key={i}>
                {response?.assistants
                  ?.filter((item) => {
                    if (selectedCategory === "All") return true;
                    return item.category === selectedCategory;
                  })
                  .map((item, index) => (
                    <InventoryItem
                      assistantId={item.assistantId}
                      assistantName={item.assistantName}
                      facingDescription={item.facingDescription}
                      modelPreview={item.modelPreview}
                      creator={item.creator}
                      isLast={index === response.assistants.length - 1}
                      hitCurrentLastItem={() => {
                        if (hasNextPage) {
                          void fetchNextPage();
                        }
                      }}
                      key={item.assistantId}
                      {...itemProps}
                    />
                  ))}
              </Fragment>
            ))}

            {isFetchingNextPage && (
              <>
                {Array.from(Array(10).keys()).map((i) => (
                  <InventoryItemSkeleton key={i} />
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NPCInventory;
